import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * ShowcaseCarousel — a luxurious, scroll-snapping horizontal media carousel.
 * Auto-advances every 6 s, pauses on hover, supports keyboard arrows and
 * manual nav buttons + position dots.
 *
 * `slides` is an array of either:
 *   { type: "video", src }
 *   { type: "image", src, alt }
 */
const SLIDES = [
    {
        type: "video",
        src: "/video/nova.mp4",
    },
    {
        type: "image",
        src: "/img/optim/nv2lpk59_Untitled_20design_20_287_29.webp",
        alt: "FlowLux smart toilet — outdoor wood-deck install",
        zoom: undefined,
    },
    {
        type: "image",
        src: "/img/optim/u3qlpv4i_Untitled_20design_20_288_29.webp",
        alt: "FlowLux smart toilet — Gucci-tile bathroom",
    },
    {
        type: "video",
        src: "/video/pulsar.mp4",
    },
    {
        type: "image",
        src: "/img/optim/rekkt46z_Untitled_20design_20_2810_29.webp",
        alt: "FlowLux smart toilet — hex-tile bathroom",
    },
    {
        type: "image",
        src: "/img/optim/k69rl1c5_Untitled_20design_20_2811_29.webp",
        alt: "FlowLux smart toilet — open lid install",
    },
    {
        type: "image",
        src: "/img/optim/ya66nr4c_Untitled_20design_20_2812_29.webp",
        alt: "FlowLux smart toilet — night-light blue glow",
    },
    {
        type: "image",
        src: "/img/optim/a97xknec_Untitled_20design_20_2813_29.webp",
        alt: "FlowLux smart toilet — open lid blue interior",
    },
];

export default function ShowcaseCarousel() {
    const trackRef = useRef(null);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);

    const scrollToIndex = (i) => {
        const idx = ((i % SLIDES.length) + SLIDES.length) % SLIDES.length;
        const track = trackRef.current;
        if (!track) return;
        const slide = track.children[idx];
        if (slide) {
            track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
        }
        setActive(idx);
    };

    const next = () => scrollToIndex(active + 1);
    const prev = () => scrollToIndex(active - 1);

    // Track scroll position → update active dot. rAF-throttled + reads batched
    // before any writes to avoid layout thrash / forced reflows.
    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;
        let ticking = false;
        const handle = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                ticking = false;
                // Batch all layout reads first
                const slides = Array.from(track.children);
                const trackScroll = track.scrollLeft;
                const trackWidth = track.clientWidth;
                const positions = slides.map((s) => s.offsetLeft + s.clientWidth / 2);
                // Then compute + write
                const center = trackScroll + trackWidth / 2;
                let nearest = 0;
                let nearestDist = Infinity;
                for (let i = 0; i < positions.length; i++) {
                    const d = Math.abs(positions[i] - center);
                    if (d < nearestDist) { nearestDist = d; nearest = i; }
                }
                setActive(nearest);
            });
        };
        track.addEventListener("scroll", handle, { passive: true });
        return () => track.removeEventListener("scroll", handle);
    }, []);

    // Auto-advance every 6s
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => {
            scrollToIndex(active + 1);
        }, 6000);
        return () => clearInterval(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, paused]);

    // Keyboard
    useEffect(() => {
        const handle = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        const track = trackRef.current;
        track?.addEventListener("keydown", handle);
        return () => track?.removeEventListener("keydown", handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return (
        <section
            className="border-t border-flow-line bg-white"
            data-testid="section-showcase-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-10" data-reveal>
                    <div>
                        <div className="fl-eyebrow">FlowLux Weekly Showcase</div>
                        <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl tracking-tight text-flow-ink"
                            style={{ fontWeight: 200 }}>
                            Inside Our Bathrooms
                        </h2>
                    </div>
                    <div className="hidden md:flex items-center gap-2">
                        <button
                            type="button"
                            onClick={prev}
                            aria-label="Previous slide"
                            className="w-11 h-11 rounded-full border border-flow-line hover:border-flow-blue hover:text-flow-blue text-flow-ink transition-colors flex items-center justify-center"
                            data-testid="carousel-prev"
                        >
                            <ChevronLeft size={18} strokeWidth={1.25} />
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            aria-label="Next slide"
                            className="w-11 h-11 rounded-full border border-flow-line hover:border-flow-blue hover:text-flow-blue text-flow-ink transition-colors flex items-center justify-center"
                            data-testid="carousel-next"
                        >
                            <ChevronRight size={18} strokeWidth={1.25} />
                        </button>
                    </div>
                </div>

                <div className="relative" data-reveal data-reveal-delay="1">
                    <div
                        ref={trackRef}
                        tabIndex={0}
                        className="fl-carousel-track flex gap-5 md:gap-6 overflow-x-auto snap-x snap-mandatory pb-2 -mx-2 px-2 focus:outline-none"
                        style={{ scrollbarWidth: "none" }}
                        data-testid="carousel-track"
                    >
                        {SLIDES.map((s, i) => (
                            <div
                                key={i}
                                className="snap-center shrink-0 w-[88%] sm:w-[72%] md:w-[60%] lg:w-[52%] aspect-[16/10] bg-white overflow-hidden rounded-sm shadow-[0_30px_60px_-30px_rgba(11,28,58,0.25)]"
                                data-testid={`carousel-slide-${i}`}
                            >
                                {s.type === "video" ? (
                                    <video
                                        src={s.src}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        aria-label="FlowLux smart toilet showcase video"
                                        className="block w-full h-full object-cover"
                                        style={s.zoom ? { transform: `scale(${s.zoom})`, transformOrigin: "center" } : undefined}
                                    />
                                ) : (
                                    <img
                                        src={s.src}
                                        alt={s.alt || ""}
                                        loading="lazy"
                                        className="block w-full h-full object-cover"
                                        style={{ transform: "scale(1.3)", transformOrigin: "center" }}
                                    />
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Edge fades for a luxurious feel */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-white to-transparent" />
                    <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-white to-transparent" />
                </div>

                <div className="mt-8 flex items-center justify-center gap-2" data-testid="carousel-dots">
                    {SLIDES.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => scrollToIndex(i)}
                            aria-label={`Go to slide ${i + 1}`}
                            data-testid={`carousel-dot-${i}`}
                            className={`h-[3px] transition-all duration-500 rounded-full ${i === active ? "w-10 bg-flow-blue" : "w-5 bg-flow-line hover:bg-flow-ink"}`}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
}
